/**
 * Extracts the date portion (YYYY-MM-DD) from a date string to avoid
 * timezone conversion issues. Dates like "2026-03-30T00:00:00.000000Z"
 * would otherwise shift to 3/29 in US Eastern time when parsed by
 * new Date().
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';

  // Extract just the date portion (YYYY-MM-DD) to avoid UTC→local timezone shift
  const datePart = dateString.split('T')[0];
  const parts = datePart.split('-');

  if (parts.length !== 3) return 'N/A';

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);

  if (isNaN(year) || isNaN(month) || isNaN(day)) return 'N/A';

  return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
};

export const formatDateForInput = (dateString: string): string => {
  // Extract YYYY-MM-DD directly to avoid timezone shift
  return dateString.split('T')[0];
};

export const isValidDate = (dateString: string): boolean => {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

export const getPreviousWorkingDay = (): string => {
  const date = new Date();
  // Move back at least one day
  date.setDate(date.getDate() - 1);
  // If Saturday (6), go back to Friday; if Sunday (0), go back to Friday
  if (date.getDay() === 6) {
    date.setDate(date.getDate() - 1);
  } else if (date.getDay() === 0) {
    date.setDate(date.getDate() - 2);
  }
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

export const getDaysAgo = (dateString: string): number => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return months === 1 ? '1 month ago' : `${months} months ago`;
  } else {
    const years = Math.floor(diffDays / 365);
    return years === 1 ? '1 year ago' : `${years} years ago`;
  }
};